import { IPermitTypeField, PermitFieldType } from '~/client/graph'
import { getWorkflowStepLevel } from '~/client/src/shared/constants/formStatusesTags'
import { getWorkflowStepDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import { IPermitFormField } from '~/client/src/shared/stores/ui/PermitFieldsStores/PermitFieldsBase.store'
import PermitWorkflowFieldsStore, {
  SpecificPermitFieldTypes,
} from '~/client/src/shared/stores/ui/PermitFieldsStores/PermitWorkflowFields.store'

import {
  IWorkflowSummaryField,
  IWorkflowSummaryRequest,
} from '../services/WorkflowSummary.service'

const ignoredFieldIds = new Set<string>([
  SpecificPermitFieldTypes.Type,
  SpecificPermitFieldTypes.Title,
  SpecificPermitFieldTypes.SpecificInspectionOptions,
  SpecificPermitFieldTypes.RecurringOptions,
])

const ignoredFieldTypes = new Set<string>([
  PermitFieldType.Section,
  PermitFieldType.Divider,
  PermitFieldType.Instructions,
])

interface IWorkflowSummaryMapperOptions {
  companiesStore?: CompaniesStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
}

const toIsoString = (value?: number): string | undefined => {
  if (!value) {
    return undefined
  }

  return new Date(value).toISOString()
}

const toSerializableValue = (value: any): any => {
  if (value === undefined || value === null) {
    return null
  }

  if (Array.isArray(value)) {
    return value.map(toSerializableValue)
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  if (typeof value !== 'object') {
    return value
  }

  return Object.entries(value).reduce(
    (acc, [key, item]) => {
      if (typeof item === 'function' || item === undefined) {
        return acc
      }

      acc[key] = toSerializableValue(item)
      return acc
    },
    {} as Record<string, any>,
  )
}

const resolveLocationValue = (
  value: any,
  locationAttributesStore?: LocationAttributesStore,
): any => {
  if (!locationAttributesStore) {
    return toSerializableValue(value)
  }

  if (typeof value === 'string') {
    return locationAttributesStore.getById(value)?.name || null
  }

  const location =
    locationAttributesStore.getLocationBySiteLocationObj(value) ||
    locationAttributesStore.getById(value?.id)
  if (!location) {
    return toSerializableValue(value)
  }

  return {
    name: location.name,
    type: value?.type,
  }
}

const resolveValue = (
  type: string,
  value: any,
  options: IWorkflowSummaryMapperOptions,
): any => {
  if (value === null || value === undefined) {
    return null
  }

  switch (type) {
    case PermitFieldType.Company:
    case PermitFieldType.CompanyInformational:
      return typeof value === 'string'
        ? options.companiesStore?.getCompanyNameById(value, '') || null
        : toSerializableValue(value)
    case PermitFieldType.Assignee:
    case PermitFieldType.Requester:
    case PermitFieldType.Subscriber:
    case PermitFieldType.User:
      return typeof value === 'string'
        ? options.projectMembersStore?.getById(value)?.fullName || null
        : toSerializableValue(value)
    case PermitFieldType.Location:
    case PermitFieldType.LocationInformational:
    case PermitFieldType.Equipment:
    case PermitFieldType.EquipmentInformational:
      return resolveLocationValue(value, options.locationAttributesStore)
    default:
      return toSerializableValue(value)
  }
}

const mapChecklistValues = (
  values: any[],
  checklistItems?: Array<{ id?: string; text?: string }>,
): any[] => {
  const questionTextById = new Map(
    (checklistItems || []).map(item => [item.id, item.text]),
  )

  return values.map(item => ({
    question:
      questionTextById.get(item?.checklistItemId) || item?.checklistItemId,
    answer:
      item?.answer === true
        ? 'yes'
        : item?.answer === false
          ? 'no'
          : 'unanswered',
  }))
}

const mapFieldValues = (
  permit: Workflow,
  workflowFieldsStore: PermitWorkflowFieldsStore,
  field: {
    id?: string
    type?: string
    tableId?: string
    parentInstanceId?: string
    parentValueIndex?: number
    checklistItems?: Array<{ id?: string; text?: string }>
  },
  options: IWorkflowSummaryMapperOptions,
): any[] => {
  if (field.type === PermitFieldType.StartFinishDates) {
    return [
      {
        startDate: toIsoString(permit.startDate),
        endDate: toIsoString(permit.endDate),
        isAllDay: permit.isAllDay,
      },
    ]
  }

  if (!field.id) {
    return []
  }

  const values = workflowFieldsStore.getFieldValues<any>(
    field.id,
    field.tableId,
    undefined,
    false,
    field.parentInstanceId,
    field.parentValueIndex,
  )

  if (
    field.type === PermitFieldType.Checklist ||
    field.type === PermitFieldType.Question
  ) {
    return mapChecklistValues(values, field.checklistItems)
  }

  return values
    .map(value => resolveValue(field.type, value, options))
    .filter(value => value !== null)
}

const mapFormField = (
  permit: Workflow,
  workflowFieldsStore: PermitWorkflowFieldsStore,
  field: IPermitFormField,
  options: IWorkflowSummaryMapperOptions,
): IWorkflowSummaryField => ({
  name: field.caption || field.type,
  type: field.type,
  values: mapFieldValues(permit, workflowFieldsStore, field, options),
})

const mapStepField = (
  permit: Workflow,
  workflowFieldsStore: PermitWorkflowFieldsStore,
  field: IPermitTypeField,
  options: IWorkflowSummaryMapperOptions,
): IWorkflowSummaryField => ({
  name: field.caption || field.type,
  type: field.type,
  values: mapFieldValues(
    permit,
    workflowFieldsStore,
    {
      ...field,
      checklistItems: field.checklist?.list || [],
    },
    options,
  ),
})

const shouldIncludeField = ({
  type,
  values,
}: IWorkflowSummaryField): boolean => {
  if (ignoredFieldTypes.has(type)) {
    return false
  }

  if (type === PermitFieldType.StartFinishDates) {
    return true
  }

  return values.length > 0
}

export const mapWorkflowSummaryRequest = (
  permit: Workflow,
  workflowFieldsStore: PermitWorkflowFieldsStore,
  options: IWorkflowSummaryMapperOptions = {},
): IWorkflowSummaryRequest => {
  const steps = workflowFieldsStore.availableWorkflowSteps
  const currentStep = steps.find(
    step => step.id === permit.currentWorkflowStepId,
  )
  const currentStepLevel = currentStep
    ? getWorkflowStepLevel(currentStep.id, steps)
    : null

  const fields = workflowFieldsStore.fields
    .filter(
      field =>
        !ignoredFieldIds.has(field.id) && field.type !== PermitFieldType.Table,
    )
    .map(field => mapFormField(permit, workflowFieldsStore, field, options))
    .filter(shouldIncludeField)

  return {
    kind: permit.isDraft ? 'workflow' : 'sitePermit',
    data: {
      title: permit.title,
      workflowName: workflowFieldsStore.template?.name,
      code: permit.code,
      status: permit.status,
      startDate: toIsoString(permit.startDate),
      endDate: toIsoString(permit.endDate),
      currentStepName: currentStep
        ? getWorkflowStepDisplayName(currentStep.type, currentStepLevel)
        : undefined,
      currentStepType: currentStep?.type,
      fields,
      steps: steps.reduce((list, step) => {
        const stepFields = (step.fields || [])
          .filter(field => field.type !== PermitFieldType.Table)
          .map(field =>
            mapStepField(permit, workflowFieldsStore, field, options),
          )
          .filter(shouldIncludeField)

        if (!stepFields.length && step.id !== permit.currentWorkflowStepId) {
          return list
        }

        list.push({
          name: getWorkflowStepDisplayName(
            step.type,
            getWorkflowStepLevel(step.id, steps),
          ),
          type: step.type,
          fields: stepFields,
        })

        return list
      }, []),
    },
  }
}
